import './styles.css';
import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Trash2 } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Select } from './components/ui/select';
import { Card, CardHeader, CardTitle, CardContent } from './components/ui/card';
// import { Switch } from './components/ui/switch';

const GradeWeightEquivalent = { S: 10, A: 9, B: 8, C: 7, D: 6, E: 5 };

const GradeImprovementCalculatorMobile = () => {
  // Initialize with one default row to ensure the form is visible when the page loads
  const [rows, setRows] = useState([{ id: Date.now(), subName: '', subCredit: '', oldGrade: '', newGrade: 'S' }]);
  const [totalCredits, setTotalCredits] = useState('');
  const [excludeA, setExcludeA] = useState(false);
  const [excludeB, setExcludeB] = useState(false);
  const [currentCGPA, setCurrentCGPA] = useState('');
  const [increaseCGPA, setIncreaseCGPA] = useState(0);

  const calculateCGPAIncrease = useCallback(() => {
    const toMinus = (excludeA ? 2 : 0) + (excludeB ? 1 : 0);
    const effectiveTotalCredits = parseInt(totalCredits) - toMinus;

    const totalJump = rows.reduce((sum, row) => {
      const jump = parseInt(row.subCredit) * (GradeWeightEquivalent[row.newGrade] - GradeWeightEquivalent[row.oldGrade]);
      return sum + (isNaN(jump) ? 0 : jump);
    }, 0);

    const increase = totalJump / effectiveTotalCredits;
    setIncreaseCGPA(isNaN(increase) ? 0 : increase.toFixed(2));
  }, [rows, totalCredits, excludeA, excludeB]);

  useEffect(() => {
    calculateCGPAIncrease();
  }, [calculateCGPAIncrease]);

  const addRow = () => {
    setRows([...rows, { id: Date.now(), subName: '', subCredit: '', oldGrade: '', newGrade: 'S' }]);
  };

  const updateRow = (id, field, value) => {
    setRows(rows.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  const deleteRow = (id) => {
    setRows(rows.filter(row => row.id !== id));
  };

  return (
    <div className="container mx-auto p-4 bg-gradient-to-br from-indigo-50 to-teal-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-6 text-center text-indigo-700">Grade Improvement Calculator</h1>

      <Card className="mb-6 bg-white shadow-lg rounded-lg overflow-hidden">
        <CardHeader className="bg-indigo-600 text-white p-4">
          <CardTitle>Subject Details</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-indigo-100 rounded-t-lg">
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Subject</th>
                  <th className="p-2 text-left">Credits</th>
                  <th className="p-2 text-left">Old</th>
                  <th className="p-2 text-left">New</th>
                  <th className="p-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.id} className="border-b border-indigo-100">
                    <td className="p-2 text-center">{index + 1}</td>
                    <td className="p-2">
                    <Input
                    value={row.subName}
                    onChange={(e) => updateRow(row.id, 'subName', e.target.value)}
                    placeholder="Name"
                    className="w-full border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 text-xs" // Change text-sm to text-xs for smaller text
                    />

                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        min="0"
                        max="5"
                        value={row.subCredit}
                        onChange={(e) => updateRow(row.id, 'subCredit', e.target.value)}
                        placeholder="Credits"
                        className="w-full border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 text-xs"
                      />
                    </td>
                    <td className="p-1">
                      <Select
                        value={row.oldGrade}
                        onChange={(e) => updateRow(row.id, 'oldGrade', e.target.value)}
                        className="w-full border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 text-xs"
                      >
                        <option value="" className="text-xs">Grade</option>
                        {Object.keys(GradeWeightEquivalent).map((grade) => (
                          <option key={grade} value={grade}>{grade}</option>
                        ))}
                      </Select>
                    </td>
                    <td className="p-2">
                      <Select
                        value={row.newGrade}
                        onChange={(e) => updateRow(row.id, 'newGrade', e.target.value)}
                        className="w-full border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500 text-xs"
                      >
                        <option value="" className="text-xs">Grade</option>
                        {Object.keys(GradeWeightEquivalent).map((grade) => (
                          <option key={grade} value={grade}>{grade}</option>
                        ))}
                      </Select>
                    </td>
                    <td className="p-2">
                      <Button
                        variant="destructive"
                        size="small"
                        onClick={() => deleteRow(row.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            onClick={addRow}
            size="small"
            className="mt-4 bg-teal-500 hover:bg-teal-600 text-white flex items-center rounded px-4 py-2"
          >
            <PlusCircle className="mr-2 h-4 w-4" /> Add Subject
          </Button>
        </CardContent>
      </Card>

      <Card className="mb-6 bg-white shadow-lg rounded-lg overflow-hidden">
        <CardHeader className="bg-teal-600 text-white p-4">
          <CardTitle>Additional Information</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label htmlFor="totalCredits" className="text-sm font-medium text-gray-700">Total credits completed:</label>
              <Input
                id="totalCredits"
                type="number"
                min="0"
                max="999"
                value={totalCredits}
                onChange={(e) => setTotalCredits(e.target.value)}
                className="w-24 text-sm border border-teal-200 rounded focus:ring-1 focus:ring-teal-500"
              />
            </div>
            <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Completed 'HUM0001: Ethics and Values'?</span>
                <button
                    onClick={() => setExcludeA(!excludeA)}
                    className={`relative w-28 h-8 flex items-center rounded-full p-1 transition-colors duration-100 ${
                    excludeA ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                >
                    <span
                    className={`absolute w-28 h-6 bg-white rounded-full shadow transition-transform duration-100 transform ${
                        excludeA ? 'translate-x-1' : 'translate-x-0'
                    }`}
                    />
                </button>
            </div>

            <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700">Completed 'CSE0001: Digital Literacy'?</span>
                <button
                    onClick={() => setExcludeB(!excludeB)}
                    className={`relative w-28 h-8 flex items-center rounded-full p-1 transition-colors duration-100 ${
                    excludeB ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                >
                    <span
                    className={`absolute w-28 h-6 bg-white rounded-full shadow transition-transform duration-100 transform ${
                        excludeB ? 'translate-x-1' : 'translate-x-0'
                    }`}
                    />
                </button>
            </div>

          </div>
        </CardContent>
      </Card>

      <Card className="bg-white shadow-lg rounded-lg overflow-hidden">
        <CardHeader className="bg-indigo-600 text-white p-4">
          <CardTitle>Results</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label htmlFor="currentCGPA" className="text-sm font-medium text-gray-700">Current CGPA:</label>
              <Input
                id="currentCGPA"
                type="number"
                min="0"
                max="10"
                step="0.01"
                value={currentCGPA}
                onChange={(e) => setCurrentCGPA(e.target.value)}
                className="w-24 text-sm border border-indigo-200 rounded focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div className="text-center text-lg font-semibold text-teal-600">
              CGPA Increase: +{increaseCGPA}
            </div>
            <div className="text-center text-lg font-semibold text-teal-600">
              Expected CGPA: {(parseFloat(currentCGPA || 0) + parseFloat(increaseCGPA || 0)).toFixed(2)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GradeImprovementCalculatorMobile;
